import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useTicketingIntegrations from "../../../../api/hooks/useTicketingIntegrations";
import {
  asanaTicketKeys,
  jiraIssuesKeys,
  serviceNowTicketKeys,
} from "../../../../api/queryKeys";
import IssueCreationModal from "./IssueCreationModal";
import JiraIssueEditModal from "./IssueEditModal";
import IssueLinkingModal from "./IssueLinkingModal";
import JiraButton from "./JiraButton";

export default function JiraInteractionManager({
  fullControl,
  vulnerability,
  isPerMisconfiguration = false,
  selectedMisconfigurations = [],
  sx,
  hideTitle = false,
  hideTooltip = false,
  hideLinkIcon = false,
}) {
  const [isJiraCreationModalOpen, setIsJiraCreationModalOpen] = useState(false);
  const [isJiraEditModalOpen, setIsJiraEditModalOpen] = useState(false);
  const [isJiraLinkingModalOpen, setIsJiraLinkingModalOpen] = useState(false);

  const jiraIssueDetails = {
    project: null,
    issuetype: null,
    summary: "",
    description: "",
    assignee: null,
    labels: [],
    entity_ids: [],
  };

  const {
    getJiraIssueByControlID,
    getJiraIssueByVulnerabilityId,
    getTicketByMisconfigurationId,
    trackEvent,
    getTicketByControlID,
    getServiceNowIncidentByControlID,
  } = useAPI();

  const {
    isJiraIntegrated,
    isAsanaIntegrated,
    isServiceNowIntegrated,
    isLoading: isLoadingIntegrations,
  } = useTicketingIntegrations();

  const { data: jiraIssue, isLoading: isLoadingJiraIssue } = useQuery({
    queryKey: vulnerability
      ? jiraIssuesKeys.jiraIssueVulnerability(vulnerability?.vulnerability_id)
      : isPerMisconfiguration
      ? jiraIssuesKeys.jiraIssueMisconfiguration(
          selectedMisconfigurations[0]?.misconfiguration_id
        )
      : jiraIssuesKeys.jiraIssueControl(fullControl?.control_id),
    queryFn: async () => {
      let issue;
      if (vulnerability) {
        issue = (
          await getJiraIssueByVulnerabilityId(vulnerability.vulnerability_id)
        ).data;
      } else if (isPerMisconfiguration) {
        issue = (
          await getTicketByMisconfigurationId(
            selectedMisconfigurations[0].misconfiguration_id
          )
        ).data;
      } else {
        issue = (await getJiraIssueByControlID(fullControl?.control_id)).data;
      }
      return issue;
    },
    enabled:
      (fullControl !== null || vulnerability !== null) &&
      isJiraIntegrated &&
      !isLoadingIntegrations,
  });

  const { data: asanaTicket, isLoading: isLoadingAsanaTicket } = useQuery({
    queryKey: asanaTicketKeys.asanaTicketControl(fullControl?.control_id),
    queryFn: async () => {
      const ticket = (await getTicketByControlID(fullControl?.control_id)).data;
      return ticket;
    },
    enabled: !!fullControl && isAsanaIntegrated && !isLoadingIntegrations,
  });

  const { data: serviceNowIncident, isLoading: isLoadingServiceNowIncident } =
    useQuery({
      queryKey: serviceNowTicketKeys.serviceNowTicketControl(
        fullControl?.control_id
      ),
      queryFn: async () => {
        const incident = (
          await getServiceNowIncidentByControlID(fullControl?.control_id)
        ).data;
        return incident;
      },
      enabled:
        !!fullControl && isServiceNowIntegrated && !isLoadingIntegrations,
    });

  const createJiraIssue = () => {
    setIsJiraCreationModalOpen(true);
  };

  const handleCloseJiraCreationModal = () => {
    setIsJiraCreationModalOpen(false);
    trackEvent("User closed Jira creation modal").then();
  };

  const editJiraIssue = () => {
    // TODO: Add Edit capabilities to Jira issue modal https://linear.app/breeze-security/issue/DEV-3294/add-edit-capabilities-to-jira-issue-modal
    // setJiraIssueDetails({
    //   ...jiraIssue,
    //   entity_ids: getControlIncludedMisconfiguredEntities(control),
    // });
    // setIsJiraEditModalOpen(true);
  };

  const linkJiraIssue = () => {
    setIsJiraLinkingModalOpen(true);
    trackEvent("User opened Jira linking modal").then();
  };

  const handleCloseJiraEditModal = () => {
    setIsJiraEditModalOpen(false);
    trackEvent("User closed Jira edit modal").then();
  };

  const handleCloseJiraLinkingModal = () => {
    setIsJiraLinkingModalOpen(false);
    trackEvent("User closed Jira linking modal").then();
  };

  return (
    <>
      {/* JIRA BUTTON */}
      {isJiraIntegrated && (
        <JiraButton
          fullControl={fullControl}
          vulnerability={vulnerability}
          isPerMisconfiguration={isPerMisconfiguration}
          selectedMisconfigurations={selectedMisconfigurations}
          createJiraIssue={createJiraIssue}
          editJiraIssue={editJiraIssue}
          linkJiraIssue={linkJiraIssue}
          jiraTicketLink={jiraIssue?.source || null}
          loading={
            isLoadingIntegrations ||
            isLoadingJiraIssue ||
            isLoadingAsanaTicket ||
            isLoadingServiceNowIncident
          }
          sx={sx}
          hideTitle={hideTitle}
          hideTooltip={hideTooltip}
          hideLinkIcon={hideLinkIcon}
          disabled={
            (!!asanaTicket?.asana_details?.task_link ||
              !!serviceNowIncident?.source) &&
            !isPerMisconfiguration &&
            !vulnerability
          }
        ></JiraButton>
      )}

      {/* JIRA CREATION MODAL */}
      {isJiraCreationModalOpen && isJiraIntegrated && (
        <IssueCreationModal
          open={isJiraCreationModalOpen}
          onClose={handleCloseJiraCreationModal}
          fullControl={fullControl}
          vulnerability={vulnerability}
          isPerMisconfiguration={isPerMisconfiguration}
          selectedMisconfigurations={selectedMisconfigurations}
        />
      )}

      {/* JIRA EDIT MODAL */}
      {isJiraEditModalOpen && isJiraIntegrated && (
        <JiraIssueEditModal
          open={isJiraEditModalOpen}
          onClose={handleCloseJiraEditModal}
          initialIssueDetails={jiraIssueDetails}
          gapID={fullControl?.control_id}
        />
      )}

      {/* JIRA LINK EXISTING ISSUE MODAL */}
      {isJiraLinkingModalOpen && isJiraIntegrated && (
        <IssueLinkingModal
          open={isJiraLinkingModalOpen}
          onClose={handleCloseJiraLinkingModal}
          gapId={fullControl?.control_id || null}
          vulnerabilityId={vulnerability?.vulnerability_id || null}
          // We only allow one misconfiguration to be linked for now
          misconfigurationId={
            isPerMisconfiguration
              ? selectedMisconfigurations[0]?.misconfiguration_id
              : null
          }
        />
      )}
    </>
  );
}
